import changeBranchPopup from "@DSG/Handlers/ChangeBranch";
import CheckCircle from "@DSG/Icons/CheckCircle";
import ChevronSmall from "@DSG/Icons/ChevronSmall";
import Pickup from "@DSG/Icons/Pickup";
import Truck from "@DSG/MobiusOverrides/Icons/Truck";
import Popover from "@DSG/MobiusOverrides/Popover/Popover";
import colorPallete, { bodyFonts, elevation, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import {
    getFulfillmentLabel,
    getIsPunchOutSession,
    getSettingsCollection,
} from "@DSG/Overrides/Store/Context/ContextSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";

import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";

import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
// import { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    fulfillmentLabel: getFulfillmentLabel(state),
    isDrawerOpen: state.components.addressDrawer.isOpen,
    isPunchOutSession: getIsPunchOutSession(state),
    session: state.context.session,
    accountSettings: getSettingsCollection(state).accountSettings,
    changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
    dealerDetailsUrl: getPageLinkByPageType(state, "dealerDetails")?.url,
    shipToState: getShipToState(state, state.context.session.shipToId),
});

const mapDispatchToProps = {
    setFulfillmentMethod,
    changeBranchPopup,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    HasToasterContext;

export interface HeaderShipToAddressStyles {
    popover: InjectableCss;
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    pickUpLocation?: PickUpLocationStyles;
    boxGroupWrapper?: InjectableCss;
    radioMethodWrapper?: InjectableCss;
    radioMethodContainer?: InjectableCss;
    accordionWrapper?: InjectableCss;
    fulfillmentMethodContent?: InjectableCss;
    boxWrapper?: InjectableCss;
}
export interface WarehouseAddressInfoDisplayStyles {
    wrapper?: InjectableCss;
    nameText?: TypographyProps;
    address?: AddressInfoDisplayStyles;
}
export interface PickUpLocationStyles {
    wrapper?: InjectableCss;
    headerText?: TypographyProps;
    changeLink?: LinkPresentationProps;
    warehouseAddress?: WarehouseAddressInfoDisplayStyles;
    // findLocationModal?: FindLocationModalStyles;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerShipToAddressStyles: HeaderShipToAddressStyles = {
    popover: {
        css: css`
            padding: 16px;
            max-height: unset;
            box-shadow: ${elevation.dp08};
            border: 1px solid ${colorPallete.grey300};
            border-bottom: 0;
            position: absolute;
            top: calc(100% + 10px);
            left: 20px;
            border-radius: 0;
            margin: 0;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            top: calc(100% + 5px);
                            left: calc(0% - 16px);
                            width: calc(100% + 32px);
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
        `,
    },
    titleClickable: {
        css: css`
            width: auto;
            color: ${colorPallete.grey800};

            &[aria-expanded="true"] {
                [data-test-selector="header_shipToLabel"] {
                    span:not(.branch-tag) {
                        color: ${colorPallete.primaryRed500};
                    }
                    [class*="IconWrapper-"] {
                        transform: rotate(180deg);
                        svg {
                            stroke: ${colorPallete.primaryRed500};
                        }
                    }
                }
            }
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 8px;
            [class*="IconWrapper-"] {
                margin-left: 4px;
            }
            .fullfilment-wrapper {
                display: flex;
                align-items: center;
                .branch-tag {
                    font-size: ${bodyFonts.body2RegularSm.fontSize};
                    line-height: ${bodyFonts.body2RegularSm.lineHeight};
                    font-weight: ${bodyFonts.body2RegularSm.fontWeight};
                    span {
                        font-size: ${bodyFonts.body2SemiBoldSm.fontSize};
                        line-height: ${bodyFonts.body2SemiBoldSm.lineHeight};
                        font-weight: ${bodyFonts.body2SemiBoldSm.fontWeight};
                        margin-left: 8px;
                    }
                }
            }
            @media screen and (max-width: 768px) {
                .fullfilment-wrapper {
                    .branch-tag {
                        font-size: ${bodyFonts.body2RegularSm.fontSize};
                        line-height: ${bodyFonts.body2RegularSm.lineHeight};
                        font-weight: ${bodyFonts.body2RegularSm.fontWeight};
                        span {
                            font-size: ${bodyFonts.body2SemiBoldSm.fontSize};
                            line-height: ${bodyFonts.body2SemiBoldSm.lineHeight};
                            font-weight: ${bodyFonts.body2SemiBoldSm.fontWeight};
                        }
                    }
                }
            }
        `,
        ellipsis: true,
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
    pickUpLocation: {
        wrapper: {
            css: css`
                margin-top: 16px;
                max-width: 350px;

                [data-test-selector="headerShipToAddress_warehouseName"],
                .phone-text {
                    font-size: ${bodyFonts.body2RegularLg.fontSize};
                    line-height: ${bodyFonts.body2RegularLg.lineHeight};
                }

                .change-link {
                    color: ${colorPallete.primaryRed500};
                    font: ${bodyFonts.body2SemiBoldSm.fontWeight} ${bodyFonts.body2SemiBoldSm.fontSize} /
                        ${bodyFonts.body2SemiBoldSm.lineHeight} ${bodyFonts.body2SemiBoldSm.fontFamily};
                }
            `,
        },
        headerText: {
            css: css`
                display: block;
                color: ${colorPallete.grey800};
                margin-bottom: 8px;
                font: ${bodyFonts.body2SemiBoldSm.fontWeight} ${bodyFonts.body2SemiBoldSm.fontSize} /
                    ${bodyFonts.body2SemiBoldSm.lineHeight} ${bodyFonts.body2SemiBoldSm.fontFamily};
            `,
        },
        changeLink: {
            css: css`
                margin-left: 0;
                &:not(.view-link) {
                    margin-top: 15px;
                }
                &,
                [class*="StyledTypography"] {
                    color: ${colorPallete.primaryRed500};
                    font: ${bodyFonts.body2SemiBoldSm.fontWeight} ${bodyFonts.body2SemiBoldSm.fontSize} /
                        ${bodyFonts.body2SemiBoldSm.lineHeight} ${bodyFonts.body2SemiBoldSm.fontFamily};
                }
            `,
        },
        warehouseAddress: {
            address: {
                phoneText: {
                    size: bodyFonts.body2RegularLg.fontSize,
                    lineHeight: bodyFonts.body2RegularLg.lineHeight,
                    color: colorPallete.grey800,
                    weight: bodyFonts.body2RegularLg.fontWeight,
                },
            },
            nameText: {
                as: "p",
                size: bodyFonts.body2RegularLg.fontSize,
                lineHeight: bodyFonts.body2RegularLg.lineHeight,
                color: colorPallete.grey800,
                weight: bodyFonts.body2RegularLg.fontWeight,
                css: css`
                    margin-bottom: 0;
                    font: ${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize} /
                        ${bodyFonts.body2RegularLg.lineHeight} ${bodyFonts.body2RegularLg.fontFamily};
                    &:last-child {
                        margin-bottom: 8px;
                    }
                `,
            },
        },
    },
    boxGroupWrapper: {
        css: css`
            flex-direction: row;
            legend {
                display: none;
            }

            /* span[class*="TypographyStyle"] {} */
        `,
    },
    boxWrapper: {
        css: css`
            display: flex;
            input {
                appearance: none;
                margin: 0;
                & + label {
                    row-gap: 4px;
                    border: 1px solid ${colorPallete.grey600};
                    padding: 15px 16px;
                    border-radius: 8px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    cursor: pointer;
                    width: 171px;
                    margin: 0;
                    font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                        ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                    &:before,
                    &:after {
                        display: none;
                    }
                    svg {
                        width: 24px;
                        height: 24px;
                    }

                    @media only screen and (max-width: 540px) {
                        min-height: 100px;
                    }
                }
                &:checked {
                    & + label {
                        padding: 14px 16px;
                        &:before,
                        &:after {
                            display: none;
                        }
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            input {
                                & + label {
                                    padding: 23px 16px;
                                    width: 185px;
                                    font-size: ${bodyFonts.body2RegularLg.fontSize};
                                    line-height: ${bodyFonts.body2RegularLg.lineHeight};
                                }
                                &:checked {
                                    & + label {
                                        padding: 22px 16px;
                                    }
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    radioMethodContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            column-gap: 16px;
        `,
    },
    radioMethodWrapper: {
        css: css`
            position: relative;
            width: 171px;
            color: ${colorPallete.grey800};
            > .checkIcon {
                display: none;
                position: absolute;
                right: 8px;
                top: 8px;
            }
            &:not(.active):hover {
                input {
                    & + label {
                        border-color: ${colorPallete.grey600};
                        box-shadow: ${elevation.dp04};
                    }
                }
            }
            &:not(.active):hover,
            &.active {
                > .checkIcon {
                    display: block;
                }
            }
            &.active {
                input {
                    & + label {
                        border: 2px solid ${colorPallete.grey800};
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 171px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
}

// const styles = headerShipToAddressStyles;
const HeaderShipToAddress: FC<Props> = ({
    fulfillmentLabel,
    session,
    accountSettings,
    changeCustomerUrl,
    dealerDetailsUrl,
    shipToState,
    changeBranchPopup,
    setFulfillmentMethod,
}) => {
    const [isLinkClicked, setIsLinkClicked] = React.useState(false);
    const handleClickAddress = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setIsLinkClicked(!isLinkClicked);
    };
    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);

    const list = React.createRef();
    const popover = React.createRef<any>();

    const fulfillmentMethodChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFulfillmentMethod({ fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp" });
    };
    const changeBranchPopupHandler = (value: boolean) => {
        changeBranchPopup({ changeBranch: value });
        let popoverElement : any;
        let shipToButtonElement : any;
        if(typeof window !== 'undefined')
        {
            popoverElement = document.querySelector(".popover");
            const body = document.querySelector("body") as HTMLBodyElement;
            if ( popoverElement) {
                popoverElement.style.display = 'none';
                shipToButtonElement = document.querySelector("#header_shipToButton");// .ariaExpanded=false;
                shipToButtonElement.ariaExpanded = false;
            }

            if (body) {
                body.classList.add('background-disable');
            }
            
        } 
    };

    const partFullfillmentLable = fulfillmentLabel.length >= 40 ? `${fulfillmentLabel.substring(0, 30)}...` : fulfillmentLabel;
    
    const shipToAddress = (
        <Clickable
            {...styles.titleClickable}
            onClick={handleClickAddress}
            data-test-selector="header_shipToButton"
            id="header_shipToButton"
        >
            {" "}
            {session.fulfillmentMethod === "Ship" ? <Icon src={Truck} /> : <Icon src={Pickup} />}
            <Typography {...styles.titleTypography} data-test-selector="header_shipToLabel">
                <div className="fullfilment-wrapper">
                    {session.fulfillmentMethod === "Ship" ? (
                        <span className="branch-tag">
                            {translate("Deliver to:")}
                            <span>{partFullfillmentLable}</span>
                        </span>
                    ) : (
                        <span className="branch-tag">
                            {translate("Pick up from:")}
                            <span>{partFullfillmentLable}</span>
                        </span>
                    )}
                    <Icon src={ChevronSmall} size={16} />
                </div>
            </Typography>
        </Clickable>
    );
    return (
        <>
            <Popover
                {...styles.popover}
                controlsId="header_shipToButton-popover"
                ref={popover}
                insideRefs={[list]}
                popoverTrigger={shipToAddress}
                positionFunction={(element: React.RefObject<HTMLUListElement>) => {
                    return { position: "absolute" };
                }}
                wrapperProps={styles.popoverWrapper}
                contentBodyProps={
                    {
                        ref: list,
                        ...styles.popoverContentBody,
                    } as any
                }
                // onClose={closePopover}
                className="popover"
                {...styles.popoverWrapper}
            >
                <RadioGroup
                    label={translate("Fulfillment Method")}
                    value={session.fulfillmentMethod}
                    onChangeHandler={fulfillmentMethodChangeHandler}
                    {...styles.boxGroupWrapper}
                    data-test-selector="headerShipToAddressFulfillmentMethod"
                >
                    <StyledWrapper {...styles.radioMethodContainer}>
                        <StyledWrapper
                            {...styles.radioMethodWrapper}
                            className={`${session.fulfillmentMethod === "Ship" ? "active" : ""} radio-group-wrapper`}
                        >
                            <Radio
                                value={FulfillmentMethod.Ship}
                                {...styles.fulfillmentMethodRadio}
                                data-test-selector="fulfillmentMethod_ship"
                                {...styles.boxWrapper}
                            >
                                <Icon size={24} src={Truck} />
                                <Typography>{translate("Delivery")}</Typography>
                            </Radio>
                            <StyledWrapper className="checkIcon">
                                <CheckCircle />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.radioMethodWrapper}
                            className={`${session.fulfillmentMethod === "PickUp" ? "active" : ""} radio-group-wrapper`}
                        >
                            <Radio
                                value={FulfillmentMethod.PickUp}
                                {...styles.fulfillmentMethodRadio}
                                data-test-selector="fulfillmentMethod_pickUp"
                                {...styles.boxWrapper}
                            >
                                <Icon size={24} src={Pickup} />
                                <Typography>{translate("Pick Up")}</Typography>
                            </Radio>
                            <StyledWrapper className="checkIcon">
                                <CheckCircle />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </RadioGroup>
                <StyledWrapper className="fulfillmentMethodContent" {...styles.fulfillmentMethodContent}>
                    {session.fulfillmentMethod === FulfillmentMethod.PickUp && session.pickUpWarehouse && (
                        <PickUpLocation
                            warehouse={session.pickUpWarehouse}
                            dealerDetailsUrl={dealerDetailsUrl as string}
                            onBranchChange={changeBranchPopupHandler}
                        />
                    )}
                    {session.fulfillmentMethod === FulfillmentMethod.Ship && (
                        <ShipToLocation
                            changeCustomerUrl={changeCustomerUrl}
                            dealerDetailsUrl={dealerDetailsUrl}
                            shipToState={shipToState}
                            fulfillmentLabel=""
                            isDrawerOpen={false}
                            isPunchOutSession={false}
                            session={session}
                            accountSettings={accountSettings}
                            // id={id}
                        /> // shipToAddress={shipToState.value}/>z
                    )}
                </StyledWrapper>
            </Popover>
        </>
    );
};

type ShipToProperties = ReturnType<typeof mapStateToProps>;

const ShipToLocation: FC<ShipToProperties> = ({ changeCustomerUrl, shipToState, dealerDetailsUrl }) => {
    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);
    const componentStyles = styles.pickUpLocation || {};

    return (
        <>
            <StyledWrapper {...componentStyles.wrapper} data-test-selector="headerShipToAddress_ShipToLocation">
                <Typography {...componentStyles.headerText}>{translate("Deliver to:")}</Typography>
                <Typography {...componentStyles.warehouseAddress?.nameText}>
                    {shipToState?.value?.address1}, {" "} {shipToState?.value?.postalCode}
                </Typography>
                <a href={changeCustomerUrl} className="text-link text-link-primary text-link-active change-link">{translate("Change delivery address")}</a>
                <Zone contentId="DSGShippingAddressContent" zoneName="DSGShipToLinks" />
            </StyledWrapper>
        </>
    );
};

interface PickUpLocationProps {
    warehouse: WarehouseModel;
    dealerDetailsUrl: string;
    onBranchChange: (value: boolean) => void;
}

const PickUpLocation: FC<PickUpLocationProps> = ({ warehouse, dealerDetailsUrl, onBranchChange }) => {
    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);
    const componentStyles = styles.pickUpLocation || {};

    const handleOpenFindLocation = () => {
        onBranchChange(true);
    };

    return (
        <>
            <StyledWrapper {...componentStyles.wrapper} data-test-selector="headerShipToAddress_pickUpLocation">
                <Typography {...componentStyles.headerText}>{translate("Pick up from:")}</Typography>
                <StyledWrapper>
                    <WarehouseAddressInfoDisplay warehouse={warehouse} dealerDetailsUrl={dealerDetailsUrl} />
                </StyledWrapper>
                <Link
                    onClick={handleOpenFindLocation}
                    {...componentStyles.changeLink}
                    data-test-selector="headerShipToAddress_pickUpLocation_findLocation"
                    className="text-link text-link-primary text-link-active change-branch-link-custom"
                >
                    {translate("Change branch")}
                </Link>
            </StyledWrapper>
        </>
    );
};

interface WarehouseAddressInfoDisplayProps {
    warehouse: WarehouseModel;
    dealerDetailsUrl: string;
}

const WarehouseAddressInfoDisplay: FC<WarehouseAddressInfoDisplayProps> = ({ warehouse, dealerDetailsUrl }) => {
    const styles = useMergeStyles("headerShipToAddress", headerShipToAddressStyles);
    const componentStyles =
        styles.pickUpLocation && styles.pickUpLocation.warehouseAddress ? styles.pickUpLocation.warehouseAddress : {};
    const componentLinkStyles = styles.pickUpLocation || {};

    const viewBranchLink = warehouse && warehouse.properties && warehouse.properties["dealerCustomUrl"] && warehouse.properties["dealerCustomUrl"] !== ""
    ? `/location?${warehouse.properties["dealerCustomUrl"]}-${warehouse.state?.toLowerCase()}`
    : `/location?${warehouse.name?.toLowerCase().replace('&','and')}-${warehouse.state?.toLowerCase()}`
    return (
        <StyledWrapper {...componentStyles.wrapper}>
            <StyledWrapper className="warehouse-address-info-display">
                <Typography
                    {...componentStyles.warehouseAddress}
                    data-test-selector="headerShipToAddress_warehouseName"
                >
                    {warehouse.description || warehouse.name}
                </Typography>
                {" "}
                <Link {...componentLinkStyles.changeLink} className="view-link" href={viewBranchLink}>({translate("View")})</Link>
            </StyledWrapper>
            <Link className="phone-text">
                {translate("Phone:")} {warehouse.phone}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderShipToAddress)),
    definition: {
        displayName: "Header Ship To Address Menu",
        icon: "location-dot",
        group: "Common",
    },
};

export default widgetModule;
